import { useContext, useEffect, useRef, useState } from 'react';
import { BASE } from '../services/api';
import { storage } from '../utils/storage';
import { AuthContext } from '../context/AuthContext';

const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];

const _defaultIsTerminal = (data) =>
  ['done', 'failed', 'cancelled'].includes(data?.status);

// Connection status is additive: existing consumers that ignore the return
// value (NotificationBell) keep working unchanged. Consumers that want to
// surface reconnect state (ScanDetail) can read `.status`.
export function useSSE(path, onMessage, { isTerminal = _defaultIsTerminal } = {}) {
  const auth = useContext(AuthContext);
  const orgId = auth ? auth.activeOrg?.id : storage.getJSON?.('active_org')?.id;
  const authToken = auth ? auth.token : storage.get('token');
  const onMessageRef = useRef(onMessage);
  const isTerminalRef = useRef(isTerminal);
  const [status, setStatus] = useState(path ? 'connecting' : 'idle');

  useEffect(() => { onMessageRef.current = onMessage; });
  useEffect(() => { isTerminalRef.current = isTerminal; });

  useEffect(() => {
    if (!path || !authToken) { setStatus('idle'); return; }

    let es = null;
    let attempt = 0;
    let timer = null;
    let stopped = false;
    const controller = new AbortController();

    const fetchSseToken = async () => {
      const token = authToken;
      if (!token) return null;
      try {
        const response = await fetch(`${BASE}/api/auth/sse-token`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(orgId ? { 'X-Active-Org': orgId } : {}),
          },
        });
        if (!response.ok) return null;
        const data = await response.json();
        return data.sse_token || null;
      } catch {
        return null;
      }
    };

    async function connect() {
      if (stopped) return;
      setStatus(attempt === 0 ? 'connecting' : 'reconnecting');
      try {
        const sseToken = await fetchSseToken();
        if (stopped) return;
        if (!sseToken) throw new Error('SSE token unavailable');
        const sep = path.includes('?') ? '&' : '?';
        es = new EventSource(`${BASE}${path}${sep}token=${encodeURIComponent(sseToken)}`);

        es.onopen = () => { if (!stopped) { setStatus('open'); attempt = 0; } };

        es.onmessage = (e) => {
          if (stopped) return;
          try {
            const data = JSON.parse(e.data);
            if (!stopped) { setStatus('open'); attempt = 0; }
            onMessageRef.current(data);
            if (isTerminalRef.current(data)) {
              stopped = true;
              es.close();
            }
          } catch (err) { console.error('[useSSE]', err); }
        };

        es.onerror = () => {
          es.close();
          if (stopped) return;
          setStatus('reconnecting');
          const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
          attempt++;
          timer = setTimeout(connect, delay);
        };
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error('[useSSE] connect error:', err);
        if (!stopped) {
          setStatus('reconnecting');
          const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
          attempt++;
          timer = setTimeout(connect, delay);
        }
      }
    }

    connect();

    return () => {
      stopped = true;
      controller.abort();
      clearTimeout(timer);
      es?.close();
    };
  }, [path, orgId, authToken]);

  return { status };
}
